// ============================================================================
// src/services/clubsApi.js - FSGU Golf Clubs API Service
// ============================================================================

const API_BASE_URL = 'https://api.fsgu.co.za';

/**
 * Fetch all golf clubs from the API
 * @param {Object} filters - Optional filters
 * @param {string} filters.city - Filter by city ('all' for no filter)
 * @param {boolean} filters.active - Only return active clubs (default: true)
 * @param {boolean} filters.withCoordinates - Only return clubs with coordinates (default: true)
 * @returns {Promise<Array>} Array of golf club objects
 */
export const fetchGolfClubs = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Apply filters
    if (filters.city && filters.city !== 'all') {
      params.append('city', filters.city);
    }
    
    // Default to active clubs only
    const activeOnly = filters.active !== undefined ? filters.active : true;
    params.append('active', activeOnly.toString());
    
    // Default to clubs with coordinates only (for mapping)
    const withCoordinates = filters.withCoordinates !== undefined ? filters.withCoordinates : true;
    params.append('with_coordinates', withCoordinates.toString());
    
    const url = `${API_BASE_URL}/get-clubs.php?${params.toString()}`;
    console.log('🔍 Fetching clubs from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'API returned unsuccessful response');
    }
    
    console.log(`✅ Successfully fetched ${data.count} clubs`);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching golf clubs:', error);
    throw error;
  }
};

/**
 * Get a single club by ID
 * @param {number} clubId - Club ID
 * @returns {Promise<Object>} Club object
 */
export const fetchClubById = async (clubId) => {
  try {
    const clubs = await fetchGolfClubs({ active: false, withCoordinates: false });
    const club = clubs.find(c => c.id === clubId);
    
    if (!club) {
      throw new Error(`Club with ID ${clubId} not found`);
    }
    
    return club;
    
  } catch (error) {
    console.error(`❌ Error fetching club ${clubId}:`, error);
    throw error;
  }
};

/**
 * Get unique cities from clubs
 * @returns {Promise<Array<string>>} Array of city names
 */
export const fetchCities = async () => {
  try {
    const clubs = await fetchGolfClubs({ active: false, withCoordinates: false });
    const cities = [...new Set(clubs.map(club => club.city))].sort();
    
    console.log(`📍 Found ${cities.length} cities:`, cities);
    return cities;
    
  } catch (error) {
    console.error('❌ Error fetching cities:', error);
    throw error;
  }
};

/**
 * Search clubs by name or city
 * @param {string} query - Search query
 * @returns {Promise<Array>} Filtered array of clubs
 */
export const searchClubs = async (query) => {
  try {
    const clubs = await fetchGolfClubs({ active: false, withCoordinates: false });
    const lowerQuery = query.toLowerCase();
    
    const results = clubs.filter(club => 
      club.name.toLowerCase().includes(lowerQuery) ||
      club.city.toLowerCase().includes(lowerQuery) ||
      (club.address && club.address.toLowerCase().includes(lowerQuery))
    );
    
    console.log(`🔎 Search "${query}" found ${results.length} results`);
    return results;
    
  } catch (error) {
    console.error('❌ Error searching clubs:', error);
    throw error;
  }
};

/**
 * Create a new golf club
 * @param {Object} clubData - Club data
 * @returns {Promise<Object>} Created club
 */
export const createClub = async (clubData) => {
  try {
    console.log('📝 Creating new club:', clubData);
    
    const response = await fetch(`${API_BASE_URL}/create-club.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clubData)
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create club');
    }

    console.log('✅ Club created successfully:', data.data);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error creating club:', error);
    throw error;
  }
};

/**
 * Update an existing golf club
 * @param {number} clubId - Club ID
 * @param {Object} clubData - Updated club data
 * @returns {Promise<Object>} Updated club
 */
export const updateClub = async (clubId, clubData) => {
  try {
    console.log(`📝 Updating club ${clubId}:`, clubData);
    
    const response = await fetch(`${API_BASE_URL}/update-club.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...clubData,
        id: clubId
      })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to update club');
    }

    console.log('✅ Club updated successfully:', data.data);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error updating club:', error);
    throw error;
  }
};

/**
 * Delete a golf club
 * @param {number} clubId - Club ID
 * @returns {Promise<Object>} Delete confirmation
 */
export const deleteClub = async (clubId) => {
  try {
    console.log(`🗑️ Deleting club ${clubId}`);
    
    const response = await fetch(`${API_BASE_URL}/delete-club.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: clubId })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to delete club');
    }

    console.log('✅ Club deleted successfully');
    return data;
    
  } catch (error) {
    console.error('❌ Error deleting club:', error);
    throw error;
  }
};

// Export API base URL for other uses
export { API_BASE_URL };